<template>
    <div class="grid gap-4 grid-cols-1 lg:grid-cols-2">
        <div 
            class="shadow rounded bg-white p-5 relative" 
            style="min-height: 300px;max-height: 358px"
            v-if="user && (user.super_admin || user.permissions.findIndex(i => i.name === 'Manage Wideband Orders') !== -1)"
        >
            <h2 class="text-xs uppercase tracking-widest">Wideband Outstanding Orders</h2>
            <template v-if="!fetchingWidebandOutstandingOrders">
                <div class="absolute left-0 top-0 bottom-0 w-full flex items-center justify-center" v-if="widebandOutstandingOrders.length === 0">
                    <div>
                        <div class="text-center mt-5"><check-circle-icon width="70" height="70" class="inline-block text-green-400"/></div>
                        <div class="text-center text-lg mt-4">You're All Caught Up</div>
                        <div class="text-center text-gray-600 mt-1">No available outstanding orders at the moment.</div>
                        <div class="text-center mt-3">
                            <app-button class="btn-blue" @click.native="fetchWidebandOutstandingOrders">Refresh</app-button>
                        </div>
                    </div>
                </div>
                <div class="mt-10 md:mt-5 md:-mx-5 md:-mb-5" style="height: 300px;" v-else>
                    <wideband-outstanding-orders 
                        :show-table-header="false" 
                        :paginate-table="false"
                    />
                </div>
            </template>
            <template v-else>
                <div class="absolute w-full h-full flex items-center justify-center left-0 top-0 bottom-0">
                    <div class="text-center mt-5">
                        <app-spinner width="30" height="30" class="text-blue-400 inline-block" />
                        <div class="mt-3 text-gray-600">Loading...</div>
                    </div>
                </div>
            </template>
        </div>
        <div 
            class="shadow rounded bg-white p-5 relative" 
            style="min-height: 300px;max-height: 358px"
            v-if="user && (user.super_admin || user.permissions.findIndex(i => i.name === 'Manage Wideband Orders') !== -1)"
        >
            <h2 class="text-xs uppercase tracking-widest">Wideband Not Ready For Service</h2>
            <template v-if="!fetchingWidebandNotRFS">
                <div class="absolute top-0 bottom-0 w-full flex items-center justify-center" v-if="widebandNotRFS.length === 0">
                    <div>
                        <div class="text-center mt-5"><check-circle-icon width="70" height="70" class="inline-block text-green-400"/></div>
                        <div class="text-center text-lg mt-4">You're All Caught Up</div>
                        <div class="text-center text-gray-600 mt-1">No available Wideband not ready for service at the moment.</div>
                        <div class="text-center mt-3">
                            <app-button class="btn-blue" @click="fetchWidebandNotRFS">Refresh</app-button>
                        </div>
                    </div>
                </div>
                <div class="mt-10 md:mt-5 md:-mx-5 md:-mb-5" style="height: 300px;" v-else>
                    <wideband-not-rfs 
                        :show-table-header="false" 
                        :paginate-table="false"
                    />
                </div>
            </template>
            <template v-else>
                <div class="absolute w-full h-full flex items-center justify-center left-0 top-0 bottom-0">
                    <div class="text-center mt-5">
                        <app-spinner width="30" height="30" class="text-blue-400 inline-block" />
                        <div class="mt-3 text-gray-600">Loading...</div>
                    </div>
                </div>
            </template>
        </div>
        <div 
            class="shadow rounded bg-white p-5 relative" 
            style="min-height: 300px;max-height: 358px;"
            v-if="user && (user.super_admin || user.permissions.findIndex(i => i.name === 'Manage TPP') !== -1)"
        >
            <h2 class="text-xs uppercase tracking-widest">Expiring TPP Domains</h2>
            <template v-if="!fetchingTppDomains">
                <div class="absolute top-0 bottom-0 w-full flex items-center justify-center" v-if="tppDomains.length === 0">
                    <div>
                        <div class="text-center mt-5"><check-circle-icon width="70" height="70" class="inline-block text-green-400"/></div>
                        <div class="text-center text-lg mt-4">You're All Caught Up</div>
                        <div class="text-center text-gray-600 mt-1">No available expiring TPP domains at the moment.</div>
                        <div class="text-center mt-3">
                            <app-button class="btn-blue" @click="fetchTppDomains">Refresh</app-button>
                        </div>
                    </div>
                </div>
                <div class="mt-10 md:mt-5 md:-mx-5 md:-mb-5" style="height: 300px;" v-else>
                    <tpp-domains 
                        :show-table-header="false" 
                        :paginate-table="false"
                        :ajax-params="tppDomainsAjaxParams"
                    />
                </div>
            </template>
            <template v-else>
                <div class="absolute w-full h-full flex items-center justify-center left-0 top-0 bottom-0">
                    <div class="text-center mt-5">
                        <app-spinner width="30" height="30" class="text-blue-400 inline-block" />
                        <div class="mt-3 text-gray-600">Loading...</div>
                    </div>
                </div>
            </template>
        </div>
        <div 
            class="shadow rounded bg-white p-5 relative" 
            style="min-height: 300px;max-height: 358px;"
            v-if="user && (user.super_admin || user.permissions.findIndex(i => i.name === 'Manage Radius') !== -1)"
        >
            <h2 class="text-xs uppercase tracking-widest">Radius Login History</h2>
            <template v-if="!fetchingRadiusLoginHistory">
                <div class="absolute top-0 bottom-0 w-full flex items-center justify-center" v-if="radiusLoginHistory.length === 0">
                    <div>
                        <div class="text-center mt-5"><check-circle-icon width="70" height="70" class="inline-block text-green-400"/></div>
                        <div class="text-center text-lg mt-4">You're All Caught Up</div>
                        <div class="text-center text-gray-600 mt-1">No available Radius login history at the moment.</div>
                        <div class="text-center mt-3">
                            <app-button class="btn-blue" @click="fetchRadiusLoginHistory">Refresh</app-button>
                        </div>
                    </div>
                </div>
                <div class="mt-10 md:mt-5 md:-mx-5 md:-mb-5" style="height: 300px;" v-else>
                    <radius-login-history 
                        :show-table-header="false" 
                        :paginate-table="false" 
                    />
                </div>
            </template>
            <template v-else>
                <div class="absolute w-full h-full flex items-center justify-center left-0 top-0 bottom-0">
                    <div class="text-center mt-5">
                        <app-spinner width="30" height="30" class="text-blue-400 inline-block" />
                        <div class="mt-3 text-gray-600">Loading...</div>
                    </div>
                </div>
            </template>
        </div>
        <div 
            class="shadow rounded bg-white p-5 relative" 
            style="min-height: 300px;max-height: 358px;"
            v-if="user && (user.super_admin || user.permissions.findIndex(i => i.name === 'Manage ConnectWise') !== -1)"
        >
            <h2 class="text-xs uppercase tracking-widest">Connectwise Open Tickets</h2>
            <template v-if="!fetchingConnectwiseTickets">
                <div class="absolute top-0 bottom-0 w-full flex items-center justify-center" v-if="connectwiseTickets.length === 0">
                    <div>
                        <div class="text-center mt-5"><check-circle-icon width="70" height="70" class="inline-block text-green-400"/></div>
                        <div class="text-center text-lg mt-4">You're All Caught Up</div>
                        <div class="text-center text-gray-600 mt-1">No available Connectwise open tickets at the moment.</div>
                        <div class="text-center mt-3">
                            <app-button class="btn-blue" @click="fetchConnectwiseTickets">Refresh</app-button>
                        </div>
                    </div>
                </div>
                <div class="mt-10 md:mt-5 md:-mx-5 md:-mb-5" style="height: 300px;" v-else>
                    <connectwise-tickets 
                        :show-table-header="false" 
                        :paginate-table="false" 
                    />
                </div>
            </template>
            <template v-else>
                <div class="absolute w-full h-full flex items-center justify-center left-0 top-0 bottom-0">
                    <div class="text-center mt-5">
                        <app-spinner width="30" height="30" class="text-blue-400 inline-block" />
                        <div class="mt-3 text-gray-600">Loading...</div>
                    </div>
                </div>
            </template>
        </div>
        <div 
            class="shadow rounded bg-white p-5 relative" 
            style="min-height: 300px;max-height: 358px;"
            v-if="user && (user.super_admin || user.permissions.findIndex(i => i.name === 'Manage N-Able') !== -1)"
        >
            <h2 class="text-xs uppercase tracking-widest">N-Able Active Issues</h2>
            <template v-if="!fetchingNableActiveIssues">
                <div class="absolute top-0 bottom-0 w-full flex items-center justify-center" v-if="nableActiveIssues.length === 0">
                    <div>
                        <div class="text-center mt-5"><check-circle-icon width="70" height="70" class="inline-block text-green-400"/></div>
                        <div class="text-center text-lg mt-4">You're All Caught Up</div>
                        <div class="text-center text-gray-600 mt-1">No available N-Able active issues at the moment.</div>
                        <div class="text-center mt-3">
                            <app-button class="btn-blue" @click="fetchNableActiveIssues">Refresh</app-button>
                        </div>
                    </div>
                </div>
                <div class="mt-10 md:mt-5 md:-mx-5 md:-mb-5" style="height: 300px;" v-else>
                    <nable-active-issues 
                        :show-table-header="false" 
                        :paginate-table="false" 
                    />
                </div>
            </template>
            <template v-else>
                <div class="absolute w-full h-full flex items-center justify-center left-0 top-0 bottom-0">
                    <div class="text-center mt-5">
                        <app-spinner width="30" height="30" class="text-blue-400 inline-block" />
                        <div class="mt-3 text-gray-600">Loading...</div>
                    </div>
                </div>
            </template>
        </div>
    </div>
</template>

<script>
    import Http from '@/helpers/Http';
    import { mapGetters } from 'vuex';

    export default {
        components: {
            CheckCircleIcon: () => import('@/icons/CheckCircleOutlined.vue'),
            WidebandOutstandingOrders: () => import('@/views/wideband/OutstandingOrders.vue'),
            WidebandNotRfs: () => import('@/views/wideband/NotRFSLocations.vue'),
            TppDomains: () => import('@/views/tpp/Domains.vue'),
            RadiusLoginHistory: () => import('@/views/freeradius/auth/Index.vue'),
            ConnectwiseTickets: () => import('@/views/connectwise/Tickets.vue'),
            NableActiveIssues: () => import('@/views/nable/ActiveIssues.vue'),
        },
        data() {
            return {
                fetchingWidebandOutstandingOrders: true,
                fetchingWidebandNotRFS: true,
                fetchingTppDomains: true,
                fetchingRadiusLoginHistory: true,
                fetchingConnectwiseTickets: true,
                fetchingNableActiveIssues: true,
                widebandOutstandingOrders: [],
                widebandNotRFS: [],
                tppDomains: [],
                radiusLoginHistory: [],
                connectwiseTickets: [],
                nableActiveIssues: [],
                tppDomainsAjaxParams: {expiring_soon: true}
            }
        },
        computed: {
            ...mapGetters({
                user: 'auth/user'
            })
        },
        mounted() {
            if (!this.user) {
                return;
            }

            if (this.user.super_admin || this.user.permissions.findIndex(i => i.name === 'Manage Wideband Orders') !== -1) {
                this.fetchWidebandOutstandingOrders();
                this.fetchWidebandNotRFS();
            }

            if (this.user.super_admin || this.user.permissions.findIndex(i => i.name === 'Manage TPP') !== -1) {
                this.fetchTppDomains();
            }

            if (this.user.super_admin || this.user.permissions.findIndex(i => i.name === 'Manage Radius') !== -1) {
                this.fetchRadiusLoginHistory();
            }

            if (this.user.super_admin || this.user.permissions.findIndex(i => i.name === 'Manage ConnectWise') !== -1) {
                this.fetchConnectwiseTickets();
            }            
            
            if (this.user.super_admin || this.user.permissions.findIndex(i => i.name === 'Manage N-Able') !== -1) {
                this.fetchNableActiveIssues();
            }            
        },
        methods: {
            fetchWidebandOutstandingOrders() {
                this.fetchingWidebandOutstandingOrders = true;
                Http.get('wideband/orders', {params: {type: 'outstanding'}})
                    .then(({data}) => {
                        this.widebandOutstandingOrders = data.data;
                        this.fetchingWidebandOutstandingOrders = false;
                    })
                    .catch(() => {
                        this.fetchingWidebandOutstandingOrders = false;
                    })
            },
            fetchWidebandNotRFS() {
                this.fetchingWidebandNotRFS = true;
                Http.get('wideband/locations-not-ready-for-service')
                    .then(({data}) => {
                        this.widebandNotRFS = data.data;
                        this.fetchingWidebandNotRFS = false;
                    })
                    .catch(() => {
                        this.fetchingWidebandNotRFS = false;
                    })
            },
            fetchTppDomains() {
                this.fetchingTppDomains = true;
                Http.get('tpp/domains')
                    .then(({data}) => {
                        this.tppDomains = data.data;
                        this.fetchingTppDomains = false;
                    })
                    .catch(() => {
                        this.fetchingTppDomains = false;
                    })
            },
            fetchRadiusLoginHistory() {
                this.fetchingRadiusLoginHistory = true;
                Http.get('freeradius/auth')
                    .then(({data}) => {
                        this.radiusLoginHistory = data.data;
                        this.fetchingRadiusLoginHistory = false;
                    })
                    .catch(() => {
                        this.fetchingRadiusLoginHistory = false;
                    })
            },
            fetchConnectwiseTickets() {
                this.fetchingConnectwiseTickets = true;
                Http.get('connectwise/tickets')
                    .then(({data}) => {
                        this.connectwiseTickets = data.data;
                        this.fetchingConnectwiseTickets = false;
                    })
                    .catch(() => {
                        this.fetchingConnectwiseTickets = false;
                    })
            },
            fetchNableActiveIssues() {
                this.fetchingNableActiveIssues = true;
                Http.get('nable/issues')
                    .then(({data}) => {
                        this.nableActiveIssues = data.data;
                        this.fetchingNableActiveIssues = false;
                    })
                    .catch(() => {
                        this.fetchingNableActiveIssues = false;
                    })
            }
        }
    }
</script>